import { useEffect, useState } from "react";

import { FormControlLabel, Radio, RadioGroup } from "@mui/material";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "store/rootReducer";

import { DropdownGlobal } from "components/DropdownGlobal";
import GeneralInput from "components/GeneralInput";
import Switch from "components/Switch";

import { isProduct, ProductId } from "multitenancy/product";

import { AttendantRoleService } from "services/attendantRole";

import { maskPhone } from "utils/format";

import {
  Container,
  ContainerBody,
  ContainerInputs,
  ContainerInputsExternal,
  ContainerRadioGroup,
  ContainerSwitch,
  LabelRadio,
  RadioBoxWrapper,
} from "./styles";

interface UnitCreateEdit {
  name: string;
  onChangeName: (e: any) => unknown;
  description: string;
  onChangeDescription: (e: any) => unknown;
  city: string;
  onChangeCity: (e: any) => unknown;
  address: string;
  onChangeAddress: (e: any) => unknown;
  addressNumber: number | undefined;
  onChangeAddressNumber: (e: any) => unknown;
  phone: string;
  onChangePhone: (e: any) => unknown;
  unit: boolean;
  onChangeUnit: (e: any) => unknown;
  extraContent: string;
  onChangeExtraContent: (e: any) => unknown;
  active: boolean;
  onChangeSwitch: (e: boolean) => unknown;
  latLng: string;
  onChangeLatLng: (e: any) => unknown;
  integrationForeignKeys: IntegrationForeignKeyForm[];
  onChangeIntegrationForeignKey: (
    integrationName: string,
    value: string,
  ) => unknown;
  setCodeDistrict: (e: number) => unknown;
  codeDistrict: number;
  fieldErrorName: boolean;
  fieldErrorDescription: boolean;
  fieldErrorCity: boolean;
  fieldErrorAddress: boolean;
  fieldErrorAddressNumber: boolean;
  fieldErrorPhone: boolean;
  fieldErrorExtraContent: boolean;
  fieldErrorCodeDistrict: boolean;
}

interface ItemDefaultUnit {
  id: number;
  name: string;
}

interface IntegrationForeignKeyForm {
  integrationName: string;
  foreignKeyId: string;
}

export const CreateEditUnit = (props: UnitCreateEdit): JSX.Element => {
  const { t } = useTranslation();
  const [unitData, setUnitData] = useState<ItemDefaultUnit[]>([]);
  const { user } = useAppSelector((state) => state.user);
  const isUnitContextProduct =
    isProduct(ProductId.Cartorio) || isProduct(ProductId.Inquerito);
  const showIntegrationForeignKeys = isUnitContextProduct
    ? !props.unit
    : true;

  const defaultUnit = (): string => {
    if (props.unit) return t("common.select");
    const found = unitData.find((item) => item.id === props.codeDistrict);
    return found ? found.name : t("common.select");
  };

  const getUnits = async () => {
    if (user) {
      const res = await AttendantRoleService.getAllByCustomerId2(
        user.customerId,
      );
      setUnitData(res.body[0]?.districts || []);
    }
  };

  useEffect(() => {
    getUnits();
  }, []);

  const handleNumber = (e: string) => {
    const numeric = e.replace(/[^0-9]/g, "");
    props.onChangeAddressNumber(numeric);
  };

  return (
    <Container>
      <ContainerBody>
        <ContainerSwitch>
          <Switch
            dataTest="switch_active"
            label={t("common.active")}
            checked={props.active}
            onChange={() => props.onChangeSwitch(!props.active)}
          />
        </ContainerSwitch>

        <ContainerInputs>
          <GeneralInput
            id="name_unit"
            name="name"
            label={t("common.name")}
            value={props.name}
            onChange={(v) => props.onChangeName(v)}
            error={props.fieldErrorName}
            maxLength={240}
            required
            autofocus
          />
          <GeneralInput
            id="description_unit"
            name="description"
            label={t("common.description")}
            value={props.description}
            onChange={(v) => props.onChangeDescription(v)}
            error={props.fieldErrorDescription}
            maxLength={340}
            required
          />
        </ContainerInputs>

        <ContainerInputs>
          <GeneralInput
            id="city_unit"
            name="city"
            label={t("labels.city")}
            value={props.city}
            onChange={(v) => props.onChangeCity(v)}
            error={props.fieldErrorCity}
            maxLength={240}
            required
          />
          <GeneralInput
            id="address_unit"
            name="address"
            label={t("common.address")}
            value={props.address}
            onChange={(v) => props.onChangeAddress(v)}
            error={props.fieldErrorAddress}
            maxLength={140}
            required
          />
        </ContainerInputs>

        <ContainerInputs>
          <GeneralInput
            id="number_unit"
            name="addressNumber"
            label={t("labels.number")}
            value={props.addressNumber?.toString() || ""}
            onChange={(v) => handleNumber(v)}
            error={props.fieldErrorAddressNumber}
            maxLength={10}
            required
          />
          <GeneralInput
            id="phone_unit"
            name="phone"
            label={t("common.phone")}
            value={props.phone}
            onChange={(v) => props.onChangePhone(maskPhone(v))}
            error={props.fieldErrorPhone}
            maxLength={15}
            required
          />
        </ContainerInputs>

        {showIntegrationForeignKeys &&
          props.integrationForeignKeys.map((integration) => {
            const sanitizedName = integration.integrationName
              .toLowerCase()
              .replace(/\s+/g, "_");
            const inputId = `external_code_${sanitizedName}`;
            const label = isUnitContextProduct
              ? t(`common.${integration.integrationName}`)
              : `${t("common.externalCode")} ${integration.integrationName}`;
            return (
              <ContainerInputsExternal key={integration.integrationName}>
                <GeneralInput
                  id={inputId}
                  name={`foreignKey-${integration.integrationName}`}
                  label={label}
                  value={integration.foreignKeyId}
                  onChange={(v) =>
                    props.onChangeIntegrationForeignKey(
                      integration.integrationName,
                      v,
                    )
                  }
                  maxLength={50}
                />
              </ContainerInputsExternal>
            );
          })}

        <ContainerInputsExternal>
          <GeneralInput
            id="external_extra_unit"
            name="extraContent"
            label={t("labels.extraContent")}
            value={props.extraContent}
            onChange={(v) => props.onChangeExtraContent(v)}
            error={props.fieldErrorExtraContent}
            maxLength={45}
            required
          />
        </ContainerInputsExternal>

        <ContainerRadioGroup>
          <LabelRadio>{t("common.unitType")}</LabelRadio>
          <RadioBoxWrapper>
            <RadioGroup
              row
              value={props.unit ? "comarca" : "vara"}
              onChange={(e) => props.onChangeUnit(e.target.value === "comarca")}
              sx={{
                "& .MuiFormControlLabel-root": { marginRight: "24px" },
                "& .MuiRadio-root": {
                  color: "#666666",
                  "&.Mui-checked": { color: "#1D9BF0" },
                },
              }}
            >
              <FormControlLabel
                value="comarca"
                control={<Radio size="small" />}
                label={t("labels.county")}
                sx={{
                  "& .MuiFormControlLabel-label": {
                    fontSize: "14px",
                    color: "#666666",
                  },
                }}
              />
              <FormControlLabel
                value="vara"
                control={<Radio size="small" />}
                label={t("labels.court")}
                sx={{
                  "& .MuiFormControlLabel-label": {
                    fontSize: "14px",
                    color: "#666666",
                  },
                }}
              />
            </RadioGroup>
          </RadioBoxWrapper>
        </ContainerRadioGroup>

        {!props.unit && (
          <ContainerInputs>
            <DropdownGlobal
              id="dropdown_unit"
              width="100%"
              labelName={t("labels.counties")}
              wantAnObject
              options={unitData}
              error={props.fieldErrorCodeDistrict}
              editDropdown={t("common.select")}
              defaultValue={defaultUnit()}
              showDefaultValue
              disabled={props.unit}
              required
              setSelected={(item: ItemDefaultUnit) =>
                props.setCodeDistrict(item.id)
              }
            />
          </ContainerInputs>
        )}
      </ContainerBody>
    </Container>
  );
};
